import React, { useEffect, useState } from "react";
import AdminLayout from "./AdminLayout";
import api from "../../services/api";

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const [statsRes, recentRes] = await Promise.all([
          api.get("/admin/dashboard"),
          api.get("/admin/recent-events"),
        ]);

        setStats(statsRes.data || {});
        setRecent(recentRes.data || []);
      } catch (err) {
        console.error("Failed to load admin dashboard:", err);
        setError(err.userMessage || err.message || "Failed to load data");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const updateEventStatus = async (id, status) => {
    try {
      await api.put(`/admin/event-status/${id}`, { status });
      // refresh stats and recent events
      const [sRes, rRes] = await Promise.all([
        api.get("/admin/dashboard"),
        api.get("/admin/recent-events"),
      ]);
      setStats(sRes.data || {});
      setRecent(rRes.data || []);
    } catch (err) {
      console.error("Failed to update status:", err);
      setError(err.userMessage || err.message || "Failed to update event status");
    }
  };

  return (
    <AdminLayout>
      {/* Hero Section */}
      <div className="dashboard-hero rounded-xl shadow-md p-8 mb-8 bg-gradient-to-r from-indigo-500/10 to-cyan-500/10">
        <h1 className="hero-title text-3xl font-bold mb-2 text-gray-800">
          Admin Dashboard
        </h1>
        <p className="hero-sub text-gray-600 text-lg">
          Welcome back! Manage events, approvals, reports, and more.
        </p>
      </div>

      {loading ? (
        <p className="text-gray-600">Loading dashboard...</p>
      ) : error ? (
        <p className="text-red-600">{error}</p>
      ) : (
        <>
          {/* Stat Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-10 mb-8 ml-4 mr-4">
            <div className="stat-card hover:scale-105 transition-transform duration-200 " style={{ background: "var(--accent1)" }}>
              <div className="stat-inner ">
                <span className="stat-value text-xl">{stats.pendingEvents ?? 0}</span>
                <span className="stat-label text-sm">Pending</span>
              </div>
            </div>

            <div className="stat-card hover:scale-105 transition-transform duration-200" style={{ background: "var(--accent2)" }}>
              <div className="stat-inner">
                <span className="stat-value text-xl">{stats.approvedEvents ?? 0}</span>
                <span className="stat-label text-sm">Approved</span>
              </div>
            </div>

            <div className="stat-card hover:scale-105 transition-transform duration-200" style={{ background: "var(--accent3)" }}>
              <div className="stat-inner">
                <span className="stat-value text-xl">{stats.cancelledEvents ?? 0}</span>
                <span className="stat-label text-sm">Cancelled</span>
              </div>
            </div>

            <div className="stat-card hover:scale-105 transition-transform duration-200" style={{ background: "linear-gradient(90deg,#9333ea,#6366f1)" }}>
              <div className="stat-inner">
                <span className="stat-value text-xl">{Math.round(stats.revenue ?? 0)}</span>
                <span className="stat-label text-sm">Revenue</span>
              </div>
            </div>
          </div>

          {/* Events Section */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-xl font-semibold mb-4 text-gray-800">Recent Event Requests</h2>

            <div className="events-grid">
              {recent.length === 0 ? (
                <p className="text-gray-500">No recent events.</p>
              ) : (
                recent.map((event) => (
                  <div key={event.id} className="event-card hover:translate-y-[-4px] transition-transform duration-200">
                    <div className="event-thumb" style={{ backgroundImage: `url(${event.image || 'https://source.unsplash.com/featured/?event'})` }}>
                      <div className={`status-badge ${event.status === 'Pending' ? 'status-pending' : event.status === 'Approved' ? 'status-approved' : 'status-rejected'}`}>
                        {event.status}
                      </div>
                    </div>
                    <div className="event-body">
                      <h3 className="event-title text-lg text-gray-800">{event.name}</h3>
                      <p className="event-desc text-gray-600 text-sm">{event.description || event.desc || ''}</p>
                      <div className="event-meta">
                        <span className="text-gray-500 text-sm">Date: {event.date}</span>
                        <div className="event-actions">
                          {event.status === 'Pending' && (
                            <>
                              <button className="bg-green-600 text-white px-3 py-1 rounded mr-2 hover:opacity-90" onClick={() => updateEventStatus(event.id, 'Approved')}>Approve</button>
                              <button className="bg-red-600 text-white px-3 py-1 rounded hover:bg-red-700" onClick={() => updateEventStatus(event.id, 'Cancelled')}>Reject</button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </>
      )}
    </AdminLayout>
  );
}
